// =============================================================================
// BRAND NORMALIZATION
// =============================================================================
// Canonical brand names, aliases, and logo path helpers.
// Ensures consistent UI display and logo keying across the app.
// =============================================================================

export type BrandType = "purchase" | "merchant";

export interface NormalizedBrand {
  canonical: string;  // Display name (e.g., "Uber Eats")
  key: string;        // File key (e.g., "uber-eats")
  aliases: string[];  // Alternative spellings/names
}

// =============================================================================
// PURCHASE BRANDS (gift cards users buy)
// =============================================================================
export const PURCHASE_BRANDS: NormalizedBrand[] = [
  { canonical: "Amazon", key: "amazon", aliases: ["amazon.ca", "amazon gift card"] },
  { canonical: "Apple", key: "apple", aliases: ["apple gift card", "itunes", "app store", "apple store"] },
  { canonical: "Google Play", key: "google-play", aliases: ["google play gift card", "google", "play store"] },
  { canonical: "Uber", key: "uber", aliases: ["uber gift card", "uber credit"] },
  { canonical: "Uber Eats", key: "uber-eats", aliases: ["uber eats gift card", "ubereats"] },
  { canonical: "Netflix", key: "netflix", aliases: ["netflix gift card"] },
  { canonical: "Spotify", key: "spotify", aliases: ["spotify gift card", "spotify premium"] },
  { canonical: "PlayStation", key: "playstation", aliases: ["playstation gift card", "psn", "ps store", "playstation store", "ps5", "ps4"] },
  { canonical: "Xbox", key: "xbox", aliases: ["xbox gift card", "microsoft xbox", "xbox live"] },
  { canonical: "Nintendo", key: "nintendo", aliases: ["nintendo gift card", "nintendo eshop", "switch"] },
  { canonical: "Steam", key: "steam", aliases: ["steam gift card", "steam wallet"] },
  { canonical: "Vanilla Visa", key: "vanilla-visa", aliases: ["visa gift card", "visa prepaid", "vanilla"] },
  { canonical: "DoorDash", key: "doordash", aliases: ["doordash gift card"] },
  { canonical: "SkipTheDishes", key: "skip-the-dishes", aliases: ["skip gift card", "skip the dishes", "skipthedishes"] },
  { canonical: "Starbucks", key: "starbucks", aliases: ["starbucks gift card", "starbucks card"] },
  { canonical: "Tim Hortons", key: "tim-hortons", aliases: ["tim hortons gift card", "tims", "timmy's"] },
  { canonical: "Boston Pizza", key: "boston-pizza", aliases: ["boston pizza gift card", "bp"] },
  { canonical: "The Keg", key: "the-keg", aliases: ["keg gift card", "keg steakhouse"] },
  { canonical: "Cineplex", key: "cineplex", aliases: ["cineplex gift card", "scene"] },
  { canonical: "Best Buy", key: "best-buy", aliases: ["best buy gift card", "bestbuy"] },
  { canonical: "Home Depot", key: "home-depot", aliases: ["home depot gift card", "homedepot"] },
  { canonical: "IKEA", key: "ikea", aliases: ["ikea gift card"] },
  { canonical: "Sephora", key: "sephora", aliases: ["sephora gift card"] },
  { canonical: "lululemon", key: "lululemon", aliases: ["lululemon gift card", "lulu"] },
  { canonical: "Gap", key: "gap", aliases: ["gap gift card"] },
  { canonical: "Old Navy", key: "old-navy", aliases: ["old navy gift card", "oldnavy"] },
  { canonical: "Indigo", key: "indigo", aliases: ["indigo gift card", "chapters", "chapters indigo"] },
  { canonical: "Hudson's Bay", key: "hudsons-bay", aliases: ["hudson's bay gift card", "hbc", "the bay"] },
  { canonical: "Petro-Canada", key: "petro-canada", aliases: ["petro canada gift card", "petro"] },
  { canonical: "Esso", key: "esso", aliases: ["esso gift card"] },
  { canonical: "Shell", key: "shell", aliases: ["shell gift card"] },
  { canonical: "Disney+", key: "disney-plus", aliases: ["disney plus gift card", "disney+", "disney"] },
  { canonical: "Roblox", key: "roblox", aliases: ["roblox gift card", "robux"] },
];

// =============================================================================
// MERCHANT BRANDS (stores selling gift cards / providing bonuses)
// =============================================================================
export const MERCHANT_BRANDS: NormalizedBrand[] = [
  { canonical: "Walmart", key: "walmart", aliases: ["walmart canada", "walmart.ca"] },
  { canonical: "Rexall", key: "rexall", aliases: ["rexall pharma plus", "rexall.ca"] },
  { canonical: "Shoppers Drug Mart", key: "shoppers-drug-mart", aliases: ["shoppers", "sdm", "shoppersdrugmart"] },
  { canonical: "Metro", key: "metro", aliases: ["metro ontario", "metro.ca"] },
  { canonical: "Sobeys", key: "sobeys", aliases: ["sobeys.com"] },
  { canonical: "Loblaws", key: "loblaws", aliases: ["loblaws.ca"] },
  { canonical: "No Frills", key: "no-frills", aliases: ["nofrills", "no frills"] },
  { canonical: "Real Canadian Superstore", key: "real-canadian-superstore", aliases: ["superstore", "rcss"] },
  { canonical: "Canadian Tire", key: "canadian-tire", aliases: ["canadiantire", "ct", "canadian tire.ca"] },
  { canonical: "Sport Chek", key: "sport-chek", aliases: ["sportchek", "sport check"] },
  { canonical: "Mark's", key: "marks", aliases: ["marks work wearhouse", "marks.com"] },
  { canonical: "Costco", key: "costco", aliases: ["costco canada", "costco.ca"] },
  { canonical: "Food Basics", key: "food-basics", aliases: ["foodbasics"] },
  { canonical: "FreshCo", key: "freshco", aliases: ["freshco.com"] },
  { canonical: "Farm Boy", key: "farm-boy", aliases: ["farmboy"] },
  { canonical: "Longo's", key: "longos", aliases: ["longos.com"] },
  { canonical: "Home Depot", key: "home-depot", aliases: ["homedepot", "home depot canada"] },
  { canonical: "Home Hardware", key: "home-hardware", aliases: ["homehardware"] },
  { canonical: "IKEA", key: "ikea", aliases: ["ikea canada"] },
  { canonical: "RONA", key: "rona", aliases: ["rona.ca"] },
  { canonical: "Giant Tiger", key: "giant-tiger", aliases: ["gianttiger"] },
  { canonical: "Dollarama", key: "dollarama", aliases: ["dollarama.com"] },
];

// =============================================================================
// NORMALIZATION FUNCTIONS
// =============================================================================

/**
 * Basic text normalization: trim, lowercase, remove punctuation, collapse spaces,
 * remove "gift card"/"giftcard"/"egift" suffix words
 */
export function normalizeBrandName(input: string): string {
  if (!input) return "";
  
  return input
    .toLowerCase()
    .trim()
    .replace(/['']/g, "'")        // Normalize apostrophes
    .replace(/[^\w\s'-]/g, " ")   // Remove punctuation except hyphen/apostrophe
    .replace(/\s+/g, " ")         // Collapse whitespace
    .replace(/\bgift\s*cards?\b/gi, "")
    .replace(/\begift\b/gi, "")
    .replace(/\bcard\b/gi, "")
    .trim();
}

/**
 * Normalize a purchase brand name to its canonical form
 */
export function normalizePurchaseBrand(raw: string): { 
  canonical: string | null; 
  key: string | null; 
  confidence: "high" | "medium" | "low" 
} {
  if (!raw || raw.trim() === "") {
    return { canonical: null, key: null, confidence: "low" };
  }
  
  const normalized = normalizeBrandName(raw);
  
  // First try exact match on canonical
  for (const brand of PURCHASE_BRANDS) {
    if (normalizeBrandName(brand.canonical) === normalized) {
      return { canonical: brand.canonical, key: brand.key, confidence: "high" };
    }
  }
  
  // Then try alias matching
  for (const brand of PURCHASE_BRANDS) {
    for (const alias of brand.aliases) {
      if (normalizeBrandName(alias) === normalized) {
        return { canonical: brand.canonical, key: brand.key, confidence: "high" };
      }
    }
  }
  
  // Partial matching (contains)
  for (const brand of PURCHASE_BRANDS) {
    const brandNorm = normalizeBrandName(brand.canonical);
    if (normalized.includes(brandNorm) || brandNorm.includes(normalized)) {
      return { canonical: brand.canonical, key: brand.key, confidence: "medium" };
    }
  }
  
  // No match found - return null (UI will use fallback)
  return { canonical: null, key: null, confidence: "low" };
}

/**
 * Normalize a merchant name to its canonical form
 */
export function normalizeMerchant(raw: string): { canonical: string; key: string } {
  if (!raw || raw.trim() === "") {
    return { canonical: raw, key: toLogoKey(raw) };
  }
  
  const normalized = normalizeBrandName(raw);
  
  // First try exact match on canonical
  for (const brand of MERCHANT_BRANDS) {
    if (normalizeBrandName(brand.canonical) === normalized) {
      return { canonical: brand.canonical, key: brand.key };
    }
  }
  
  // Then try alias matching
  for (const brand of MERCHANT_BRANDS) {
    for (const alias of brand.aliases) {
      if (normalizeBrandName(alias) === normalized) {
        return { canonical: brand.canonical, key: brand.key };
      }
    }
  }
  
  // No match - use original with generated key
  return { canonical: raw, key: toLogoKey(raw) };
}

/**
 * Convert any string to a valid logo file key
 */
function toLogoKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

/**
 * Get the logo path for a brand/merchant.
 * Returns the path if expected to exist, null otherwise.
 * Does NOT check filesystem at runtime - UI handles missing.
 */
export function toLogoPath(type: BrandType, key: string | null): string | null {
  if (!key) return null;
  
  // Only return path if we have it in our known brands
  const brands = type === "purchase" ? PURCHASE_BRANDS : MERCHANT_BRANDS;
  const found = brands.find(b => b.key === key);
  
  if (found) {
    return `/brands/${type}/${key}.svg`;
  }
  
  // Key not in known brands - UI will use fallback
  return null;
}

/**
 * Get brand display info for a raw brand name
 */
export function getPurchaseBrandInfo(raw: string | null): {
  displayName: string;
  logoPath: string | null;
  isKnown: boolean;
} {
  if (!raw) {
    return { displayName: "Select gift cards", logoPath: null, isKnown: false };
  }
  
  const { canonical, key, confidence } = normalizePurchaseBrand(raw);
  
  if (canonical && confidence !== "low") {
    return {
      displayName: canonical,
      logoPath: toLogoPath("purchase", key),
      isKnown: true,
    };
  }
  
  return { displayName: raw, logoPath: null, isKnown: false };
}

/**
 * Get merchant display info for a raw merchant name
 */
export function getMerchantBrandInfo(raw: string | null): {
  displayName: string;
  logoPath: string | null;
  isKnown: boolean;
} {
  if (!raw) {
    return { displayName: "Unknown", logoPath: null, isKnown: false };
  }
  
  const { canonical, key } = normalizeMerchant(raw);
  const logoPath = toLogoPath("merchant", key);
  
  return {
    displayName: canonical,
    logoPath,
    isKnown: logoPath !== null,
  };
}
